import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@apollo/client/react';
import { Box, Button, Center, BoxProps, Heading, HStack, Popover, SimpleGrid, Spinner, Stack, Text, VStack, Wrap } from '@chakra-ui/react';
import { UserAvatar } from '@src/components/sco/Account/UserAvatar';
import { useParentAppLayout } from '@src/components/sco/AppLayout/AppLayoutContext';
import { REQUEST_BUILDER_GROUP_REQUEST_FRAGMENT } from '@src/components/sco/Requests/RequestBuilder';
import { RequestProfileAttachmentButton } from '@src/components/sco/Requests/RequestProfile/RequestProfileAttachmentButton';
import { RequestTitleEditor } from '@src/components/sco/Requests/RequestProfile/RequestTitleEditor';
import { TypingText, useDelayedText } from '@src/components/sco/TypingText';
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { GroupRequest } from '@src/graphql-types';
import { renderCurrency } from '@src/lib/adminUtils';
import { gql } from '@src/lib/gql';
import { capitalize, uniq } from 'lodash';
import React, { useMemo } from 'react';
export type RequestProfileProps = {
  groupRequestId?: string;
  isPreview?: boolean;
  show?: {
    titleEditor?: boolean;
    bodyEditor?: boolean;
  };
};
export const RequestProfile: React.FC<RequestProfileProps> = props => {
  const appLayout = useParentAppLayout();
  const {
    getBreakpointValue
  } = useBreakpointContext();
  const query = useQuery(gql`
      query ($groupRequestId: String) {
        groupRequest(groupRequestId: $groupRequestId) {
          id
          ${REQUEST_BUILDER_GROUP_REQUEST_FRAGMENT}
          groupRequestId
          displayTitle
          title
          description
          bodyAsMarkdown
          generatedBodyAsMarkdown
          displayBodyAsMarkdown
          bodyIsGenerated
          groupRequestTypeKey
          groupRequestType {
            id
            title
            key
            colorHexCode
          }
          requestingUser {
            id
            userId
            displayName
            firstName
            lastName
            pictureUrl
            title
            ${UserAvatar.fragments.ScoUser()}
          }
          requestingCompany {
            id
            name
            stageKey
            stage {
              title
            }
            industryKeys
            industries {
              id
              industryId
              title
            }
          }
          wilAiToolSession {
            id
            toolSessionId
            threadId
            thread {
              threadId
            }
          }
          publishedAt
          publishRequestedAt
          requestingCompany {
            id
            companyId
            name
            elevatorPitch
            url
            industries {
              id
              title
              key
            }
          }
          tags {
            id
            title
            slug
            colorHexCode
            tagTypeKey
          }
          groupCommitments {
            id
            pagination {
              itemCount
            }
          }
          investorSearch {
            id
            fundingGoalInDollars
            fundingGoalStageKey
            fundingGoalStage {
              id
              title
              key
            }
            fundingGoalStructures {
              id
              title
              key
            }
            relevantInvestorTags
            investorTags {
              id
              investorTagId
              slug
              title
            }
          }
        }
      }
    `, {
    variables: {
      groupRequestId: props.groupRequestId
    }
  });
  const groupRequest: GroupRequest = query.data?.groupRequest;
  const requestVariant = useMemo<'funding' | 'person' | 'goal' | null>(() => {
    if (groupRequest?.groupRequestTypeKey) {
      if (groupRequest.groupRequestTypeKey === 'funding') {
        return 'funding';
      } else if (groupRequest.groupRequestTypeKey === 'goal') {
        return 'goal';
      } else {
        return 'person';
      }
    }
    return null;
  }, [groupRequest?.groupRequestTypeKey]);
  const delayedBodyAsMarkdown = useDelayedText(groupRequest?.bodyAsMarkdown || '', 5, props.isPreview, props.isPreview);
  const bodyAsMarkdown = props.isPreview ? delayedBodyAsMarkdown : groupRequest?.bodyAsMarkdown;
  if (query.loading) {
    return <Center padding={4} width={'full'}>
        <Spinner />
      </Center>;
  }
  if (query.error || !groupRequest) {
    return <Center padding={4}>
        <Text>
          {'Error: '}
          {query.error?.toString() || 'Not Found/Loaded'}
        </Text>
      </Center>;
  }
  return <VStack align={'stretch'} gap={4} marginBottom={getBreakpointValue([4, 4, 8])} width={'full'}>
      <VStack borderRadius={'lg'} boxShadow={'sm'} gap={6} paddingTop={getBreakpointValue([4, 4, 6])} bgColor={'colorPalette.muted'}
    // bg={`linear-gradient(to bottom, ${groupRequest.groupRequestType?.colorHexCode}80, ${groupRequest.groupRequestType?.colorHexCode}50)`}
    align={'stretch'}>
        <HStack justify={'space-between'}>
          <HStack paddingX={getBreakpointValue([4, 4, 6])}>
            <Button asChild bgColor={groupRequest.groupRequestType?.colorHexCode || null} size={'xs'}>
              <NextLink href={`/requests/browse?type=${groupRequest.groupRequestType?.key}`}>
                {groupRequest.groupRequestType?.title || 'Request'}
              </NextLink>
            </Button>
            {uniq(groupRequest.tagTitles)?.slice(0, 2).map(tagTitle => <Button key={tagTitle} asChild size={'xs'} variant={'outline'}>
                  <NextLink href={`/requests/browse?tagTitles=${tagTitle}`}>{tagTitle}</NextLink>
                </Button>)}
            {uniq(groupRequest.tagTitles)?.length > 2 && <Popover.Root>
                <Popover.Trigger asChild>
                  <Box asChild _hover={{
                bgColor: 'colorPalette.bg'
              }} bgColor={'colorPalette.bg/50'} borderRadius={'full'} color={'colorPalette.fg'} paddingX={2} paddingY={1}>
                    <button>
                      <Text textStyle={'badgeXs'}>+{uniq(groupRequest.tagTitles).length - 2}</Text>
                    </button>
                  </Box>
                </Popover.Trigger>
                <Popover.Positioner>
                  <Popover.Content bgColor={'black'} border={'none'} borderRadius={'lg'} minWidth={'xs'} overflow={'hidden'} width={'auto'}>
                    <Popover.Arrow bgColor={'black'} />
                    <Popover.CloseTrigger />
                    <Popover.Title bgColor={'black'} fontFamily={'Poppins, sans-serif'} fontSize={'xs'} fontWeight={600}>
                      Additional Tags
                    </Popover.Title>
                    <Popover.Body bgColor={'black'}>
                      <SimpleGrid columns={2} gap={1}>
                        {uniq(groupRequest.tagTitles)?.slice(2).map(tagTitle_0 => <Button key={tagTitle_0} asChild size={'xs'} variant={'outline'}>
                              <NextLink href={`/requests/browse?tagTitles=${tagTitle_0}`}>{tagTitle_0}</NextLink>
                            </Button>)}
                      </SimpleGrid>
                    </Popover.Body>
                  </Popover.Content>
                </Popover.Positioner>
              </Popover.Root>}
          </HStack>
          {/* <HStack paddingX={getBreakpointValue([4, 4, 6])}>
            <Button
              onClick={() => appLayout.togglePanel('requests-aside')}
              size={'xs'}
              variant={'outline'}
              disabled={props.isPreview}
            >
              Message
            </Button>
           </HStack> */}
        </HStack>
        <Box padding={getBreakpointValue([4, 4, 6])}>
          {props.show?.titleEditor ? <RequestTitleEditor groupRequest={groupRequest} /> : <Heading size={'2xl'}>
              {props.isPreview ? <TypingText repeat={false}>{groupRequest.displayTitle}</TypingText> : <>{groupRequest.displayTitle}</>}
            </Heading>}
        </Box>
        <Box bgColor={'colorPalette.bg/20'} borderBottomRadius={'lg'} borderTop={'1px solid'} borderTopColor={'colorPalette.bg'} padding={getBreakpointValue([4, 4, 6])}>
          <Stack align={getBreakpointValue({
          base: 'center',
          md: 'stretch'
        })} direction={getBreakpointValue({
          base: 'column',
          md: 'row'
        })} justify={getBreakpointValue({
          base: 'space-between',
          md: 'space-between'
        })}>
            <Box width={getBreakpointValue(['full', 'full', 'auto'])}>
              <HStack gap={4}>
                <UserAvatar avatarProps={{
                size: 'md'
              }} userId={groupRequest.requestingUser.userId} />
                <VStack align={'flex-start'} gap={0} width={'full'}>
                  <Heading size={'md'}>{groupRequest.requestingUser.displayName}</Heading>
                  <Text opacity={0.7} textStyle={'labelXs'}>
                    {groupRequest.requestingCompany.name}
                  </Text>
                </VStack>
              </HStack>
            </Box>
            {/* <Box width={getBreakpointValue(['full', 'full', 'auto'])}>
              <CommitButton
                size={'lg'}
                width={getBreakpointValue(['full', 'full', 'auto'])}
                isPreview={props.isPreview}
                groupRequestId={groupRequest.groupRequestId}
              />
             </Box> */}
          </Stack>
        </Box>
      </VStack>
      <Wrap>
        {requestVariant === 'funding' && <>
            <LabeledCard label={'Raise Amount'} value={renderCurrency(groupRequest.investorSearch?.fundingGoalInDollars * 100, true, false, 0)} />
            <LabeledCard label={'Stage'} value={groupRequest.investorSearch?.fundingGoalStage?.title} />
            <LabeledCard label={'Structure'} value={groupRequest.investorSearch?.fundingGoalStructures?.map(x => x.title).join(', ') || 'Any'} />
            <LabeledCard boxProps={{
          flex: 'auto',
          flexGrow: 1
        }} label={'Tags'}>
              <Wrap>
                {groupRequest.investorSearch?.relevantInvestorTags?.map(([title], idx) => <Button key={idx} bgColor={'primaryAlpha.300'} size={'xs'}>
                    {title}
                  </Button>)}
              </Wrap>
            </LabeledCard>
          </>}
        {requestVariant === 'person' && <>
            <LabeledCard label={'Seeking'} value={groupRequest.groupRequestType?.title} />
            <LabeledCard label={'Compensation'} value={groupRequest.compensationTypes?.map(t => t.title).join(', ') || 'N/A'} />
            <LabeledCard label={'Time Commitment'} value={groupRequest.timeCommitmentTypes?.map(t_0 => t_0.title).join(', ') || 'N/A'} />
            <LabeledCard boxProps={{
          flex: 'auto',
          flexGrow: 1
        }} label={'Skills Needed'}>
              {groupRequest.userSearch?.requiredSkills?.length ? <Wrap>
                  {groupRequest.userSearch?.requiredSkills?.map(([title_0], idx_0) => <Button key={idx_0} size={'xs'} variant={'outline'}>
                      {title_0}
                    </Button>)}
                </Wrap> : <Text>N/A</Text>}
            </LabeledCard>
          </>}
        {requestVariant === 'goal' && <></>}
      </Wrap>
      {groupRequest.storedFiles?.items?.length > 0 && <LabeledCard label={'Attachments'}>
          <Wrap marginTop={2}>
            {groupRequest.storedFiles.items.map(storedFile => <RequestProfileAttachmentButton key={storedFile.id} storedFile={storedFile} />)}
          </Wrap>
        </LabeledCard>}
      {/* <LabeledCard label={'Request Details'}>
        {props.show?.bodyEditor ? (
          <RequestBodyEditor groupRequest={groupRequest} />
        ) : (
          <MarkdownRenderer preset={'request'} boxProps={{ marginTop: 6 }}>
            {bodyAsMarkdown}
          </MarkdownRenderer>
        )}
       </LabeledCard> */}
      {/*<PrettyPrintJSON data={groupRequest} />*/}
    </VStack>;
};
export const generateRequestTitle = (groupRequest: GroupRequest): string => {
  const parts = [];
  if (groupRequest.requestingCompany?.stage?.title) {
    parts.push(`${groupRequest.requestingCompany?.stage?.title}-stage`);
  }
  if (groupRequest.requestingCompany?.industries?.length > 0) {
    parts.push(groupRequest.requestingCompany?.industries?.map(i => i.title).join(' '));
  }
  parts.push('startup');
  parts.push('seeking');
  if (groupRequest.groupRequestType?.title) {
    if (groupRequest.groupRequestType?.title === 'funding') {
      if (groupRequest.investorSearch?.fundingGoalInDollars) {
        parts.push(renderCurrency(groupRequest.investorSearch?.fundingGoalInDollars, true, false, 0));
        parts.push('in');
      }
      parts.push('funding');
    } else {
      parts.push(groupRequest.groupRequestType.title);
    }
  } else {
    parts.push('unspecified');
  }
  return capitalize(parts.join(' '));
};
const LabeledCard: React.FC<{
  boxProps?: Partial<BoxProps>;
  label?: any;
  value?: any;
  children?: any;
}> = props => {
  const $ = _c(12);
  const {
    getBreakpointValue
  } = useBreakpointContext();
  let t0;
  if ($[0] !== getBreakpointValue) {
    t0 = getBreakpointValue([4, 4, 6]);
    $[0] = getBreakpointValue;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== props.label) {
    t1 = <Text marginBottom={2} opacity={0.75} textStyle="labelXs">{props.label}</Text>;
    $[2] = props.label;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== props.value) {
    t2 = props.value && <Heading fontSize="md">{props.value}</Heading>;
    $[4] = props.value;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== props.boxProps || $[7] !== props.children || $[8] !== t0 || $[9] !== t1 || $[10] !== t2) {
    t3 = <Box bgColor="colorPalette.container" borderRadius="lg" boxShadow="sm" color="colorPalette.fg" flex={1} minWidth="180px" padding={t0} {...props.boxProps}>{t1}{t2}{props.children}</Box>;
    $[6] = props.boxProps;
    $[7] = props.children;
    $[8] = t0;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
};